import {usePayeesWithLogosQuery} from '@transactions/graphql';
import {AnimatePresence, motion} from 'framer-motion';
import {Check, ChevronLeft, ChevronRight, RotateCcw, Sparkles} from 'lucide-react';
import * as React from 'react';
import {automaticCategoryColor, categoryTintClasses} from 'src/categoryAppearance';
import {cn} from 'src/lib/utils';
import {LogoImage} from 'src/LogoImage';
import {CopyDescriptionButton} from 'src/RawTransactionRow';

const MAIN = 'main';
const SLIDE = {duration: 0.26, ease: [0.2, 0.8, 0.2, 1]} as const;

/**
 * The sheet's second screens (payee, category, transfer type, tags and
 * fields): each slides in over the main screen, which drifts back a little
 * as it's covered, and both reverse on the way back. The sheet's height
 * follows the screen in view on the same curve, rather than snapping when
 * one screen leaves.
 */
export const SheetScreens = ({
  screen,
  main,
  renderScreen,
}: {
  screen: string | null;
  main: React.ReactNode;
  renderScreen: (screen: string) => React.ReactNode;
}) => {
  // Each screen's height, by key, so the sheet can follow whichever is in
  // view. Keyed rather than "the latest to mount": a screen that's left and
  // then returned to mid-slide is the same element coming back, not a new
  // one, and a screen that has gone reports no height worth following.
  const [heights, setHeights] = React.useState<Record<string, number>>({});
  const observer = React.useMemo(
    () =>
      new ResizeObserver((entries) => {
        setHeights((prev) => {
          const next = {...prev};
          for (const entry of entries) {
            const node = entry.target;
            if (node instanceof HTMLElement && node.isConnected && node.dataset.screen != null) {
              next[node.dataset.screen] = node.offsetHeight;
            }
          }
          return next;
        });
      }),
    [],
  );
  React.useEffect(() => () => observer.disconnect(), [observer]);
  const observe = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (node != null) observer.observe(node);
    },
    [observer],
  );
  const height = heights[screen ?? MAIN];

  return (
    <motion.div
      initial={false}
      animate={{height: height ?? 'auto'}}
      transition={SLIDE}
      // Clipped, not hidden: a hidden overflow is still a scroll container,
      // and focusing a screen's search box would scroll it sideways.
      className="overflow-clip"
    >
      {/* One grid cell holds both screens, so the one leaving and the one
          arriving overlap while they animate instead of stacking. Each is
          opaque, and the arriving one is on top, so the screen underneath
          never shows through. */}
      <div className="grid items-start [&>*]:[grid-area:1/1]">
        <AnimatePresence initial={false}>
          {screen == null ? (
            <motion.div
              key={MAIN}
              ref={observe}
              data-screen={MAIN}
              initial={{x: '-18%', opacity: 0.6}}
              animate={{x: 0, opacity: 1}}
              exit={{x: '-18%', opacity: 0.6}}
              transition={SLIDE}
              className="bg-background z-0"
            >
              {main}
            </motion.div>
          ) : (
            <motion.div
              key={screen}
              ref={observe}
              data-screen={screen}
              initial={{x: '100%'}}
              animate={{x: 0}}
              exit={{x: '100%'}}
              transition={SLIDE}
              className="bg-background z-10"
            >
              {renderScreen(screen)}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

/** A second screen's frame: Back, its title, and an optional trailing action. */
export const ScreenFrame = ({
  title,
  onBack,
  trailing,
  children,
}: {
  title: string;
  onBack: () => void;
  trailing?: React.ReactNode;
  children: React.ReactNode;
}) => (
  // A floor on the height, so a short screen (tags and fields) doesn't make
  // the sheet drop out from under the finger that opened it.
  <div className="flex min-h-[50dvh] flex-col gap-3 sm:min-h-80">
    <div className="border-border grid grid-cols-[1fr_auto_1fr] items-center border-b pb-3">
      <button
        type="button"
        onClick={onBack}
        className="flex cursor-pointer items-center gap-0.5 justify-self-start text-sm text-blue-600 dark:text-blue-400"
      >
        <ChevronLeft className="size-4" />
        Back
      </button>
      <span className="font-semibold">{title}</span>
      <span className="justify-self-end">{trailing}</span>
    </div>
    {children}
  </div>
);

/** A row in a second screen's list. */
export const ScreenRow = ({
  onClick,
  leading,
  label,
  detail,
  selected,
  suggested,
}: {
  onClick: () => void;
  leading?: React.ReactNode;
  label: React.ReactNode;
  detail?: React.ReactNode;
  selected?: boolean;
  suggested?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'hover:bg-muted flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left',
      selected && 'bg-muted',
    )}
  >
    {leading}
    <span className="min-w-0 flex-1 truncate">{label}</span>
    {suggested && <Sparkles className="size-3.5 shrink-0 text-positive" />}
    {detail != null && (
      <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{detail}</span>
    )}
    {selected && <Check className="size-4 shrink-0 text-blue-600 dark:text-blue-400" />}
  </button>
);

export const ScreenList = ({children}: {children: React.ReactNode}) => (
  <div className="border-border divide-border overflow-hidden rounded-xl border [&>*+*]:border-t">
    {children}
  </div>
);

export const ScreenSection = ({children}: {children: React.ReactNode}) => (
  <div className="text-muted-foreground px-1 text-xs font-semibold tracking-wider uppercase">
    {children}
  </div>
);

/**
 * A line of the sheet: an icon, its value, and a chevron. Tapping it either
 * opens an editor under it (the chevron turns) or goes to a screen.
 */
export const SheetLine = ({
  icon,
  onClick,
  open,
  children,
  toScreen = false,
  wrap = false,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  open?: boolean;
  children: React.ReactNode;
  toScreen?: boolean;
  /** Content flows onto more lines rather than being cut at the edge. */
  wrap?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-expanded={toScreen ? undefined : open}
    className="flex w-full cursor-pointer items-center gap-3 py-3 text-left"
  >
    <span className="text-muted-foreground flex w-5 shrink-0 justify-center [&>svg]:size-4">
      {icon}
    </span>
    <span
      className={cn(
        'flex min-w-0 flex-1 items-center gap-1.5',
        wrap ? 'flex-wrap' : 'overflow-hidden',
      )}
    >
      {children}
    </span>
    <ChevronRight
      className={cn(
        'size-4 shrink-0 text-blue-600 transition-transform duration-200 dark:text-blue-400',
        open && 'rotate-90',
      )}
    />
  </button>
);

/**
 * A line that is its own text field: tap it and type in place, rather than
 * opening a box under it that repeats its text. Enter (which would
 * otherwise submit the sheet) or leaving it is done.
 */
export const SheetInputLine = ({
  icon,
  value,
  onChange,
  placeholder,
  onFocus,
}: {
  icon: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  onFocus?: () => void;
}) => (
  <label
    className={cn(
      'flex w-full cursor-text items-center gap-3 py-3',
      // The field has no box of its own, so focus marks the line: a bar in
      // the text inputs' ring color along its foot (inset — the sheet clips
      // anything drawn past the lines' edges).
      'has-[input:focus-visible]:shadow-[inset_0_-2px_0_var(--ring)]',
    )}
  >
    <span className="text-muted-foreground flex w-5 shrink-0 justify-center [&>svg]:size-4">
      {icon}
    </span>
    <input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onFocus={onFocus}
      placeholder={placeholder}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
      className={cn(
        'placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent outline-none placeholder:font-normal',
        valueClass(value !== ''),
      )}
    />
  </label>
);

/** An editor that grows open under its line and folds away. */
export const Expand = ({open, children}: {open: boolean; children: React.ReactNode}) => (
  <AnimatePresence initial={false}>
    {open && (
      <motion.div
        initial={{height: 0, opacity: 0}}
        animate={{height: 'auto', opacity: 1}}
        exit={{height: 0, opacity: 0}}
        transition={{duration: 0.22, ease: [0.2, 0.8, 0.2, 1]}}
        className="overflow-hidden"
      >
        <div className="pb-3">{children}</div>
      </motion.div>
    )}
  </AnimatePresence>
);

/**
 * How a value reads: a default (the bank's date, an empty field) is quiet;
 * one you entered is firm.
 */
export const valueClass = (entered: boolean) =>
  entered ? 'text-foreground font-semibold' : 'text-muted-foreground';

/** A small "filled in from history" mark. */
export const FromHistory = ({detail}: {detail?: string}) => (
  <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-positive">
    <Sparkles className="size-3" />
    {detail}
  </span>
);

/** Payee name → logo slug, from the logos you've assigned. */
export const usePayeeLogos = (): Map<string, string> => {
  const {data} = usePayeesWithLogosQuery();
  return React.useMemo(
    () =>
      new Map(
        (data?.payeesWithLogos ?? []).flatMap((p) =>
          p.logo != null ? [[p.payee, p.logo] as const] : [],
        ),
      ),
    [data],
  );
};

/** A payee's face: its logo, or its initial on a tint picked from the name. */
export const PayeeFace = ({
  name,
  logo,
  className = 'size-10 rounded-xl text-base',
}: {
  name: string;
  logo: string | undefined;
  className?: string;
}) =>
  logo ? (
    <LogoImage logo={logo} className={cn('shadow-[0_0_0_1px_var(--border)]', className)} rounded="" />
  ) : (
    <span
      aria-hidden
      className={cn(
        'flex shrink-0 items-center justify-center font-bold',
        name.trim() === '' ? 'bg-muted text-muted-foreground' : categoryTintClasses[automaticCategoryColor(name)],
        className,
      )}
    >
      {name.trim() === '' ? '?' : name.trim()[0].toUpperCase()}
    </span>
  );

/** The pill for the one decision a kind needs (a category, a transfer type). */
export const DecisionPill = ({
  onClick,
  leading,
  children,
  fixed = false,
}: {
  onClick?: () => void;
  leading?: React.ReactNode;
  children: React.ReactNode;
  fixed?: boolean;
}) => {
  const className =
    'bg-muted/60 border-border inline-flex min-w-0 max-w-full items-center gap-2 justify-self-start rounded-full border py-1.5 pr-3 pl-2 text-[15px]';
  if (fixed) {
    return (
      <span className={className}>
        {leading}
        <span className="min-w-0 truncate font-semibold">{children}</span>
      </span>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cn(className, 'cursor-pointer')}>
      {leading}
      <span className="flex min-w-0 items-center gap-1.5 truncate">{children}</span>
      <ChevronRight className="size-3.5 shrink-0 text-blue-600 dark:text-blue-400" />
    </button>
  );
};

/**
 * A bank line being used, with the amount being taken from it. Tapping the
 * amount opens a full-width row under the line to change it: Return, Done, or
 * leaving the field accepts; Escape reverts; the reset icon takes the whole
 * line again.
 */
export const BankLine = ({
  description,
  account,
  date,
  display,
  partial,
  detail,
  error,
  editor,
  onOpen,
  onCancel,
  onReset,
}: {
  description: string;
  account: React.ReactNode;
  date: string;
  display: React.ReactNode;
  partial: boolean;
  /** Under the field while editing: what the line holds. */
  detail: React.ReactNode;
  error?: string | null;
  editor: () => React.ReactNode;
  /** Opening the field: remember the value Escape goes back to. */
  onOpen: () => void;
  onCancel: () => void;
  onReset: () => void;
}) => {
  const [editing, setEditing] = React.useState(false);
  const boxRef = React.useRef<HTMLDivElement>(null);
  const open = editing || error != null;
  const finish = () => setEditing(false);
  // Opening selects the amount, so typing replaces it rather than adding to it.
  React.useEffect(() => {
    if (editing) boxRef.current?.querySelector('input')?.select();
  }, [editing]);
  const amountButton = (
    <button
      type="button"
      onClick={() => {
        onOpen();
        setEditing(true);
      }}
      aria-expanded={false}
      className={cn(
        'cursor-pointer border-b border-dashed border-blue-600/60 text-right tabular-nums dark:border-blue-400/60',
        partial ? 'text-foreground font-semibold' : 'text-muted-foreground',
      )}
    >
      {display}
    </button>
  );
  const fade = {
    initial: {opacity: 0},
    animate: {opacity: 1},
    exit: {opacity: 0},
    transition: {duration: 0.15},
  };
  return (
    // The editor takes over the line's own row — the descriptor isn't needed
    // while choosing how much of it to use — so the row keeps its height and
    // nothing below it moves.
    <div className="grid min-h-[3.75rem] px-3 py-2.5 [&>*]:[grid-area:1/1]">
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            key="edit"
            {...fade}
            ref={boxRef}
            className="flex flex-col gap-1"
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                finish();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                onCancel();
                finish();
              }
            }}
            onBlur={(event: React.FocusEvent) => {
              if (!boxRef.current?.contains(event.relatedTarget)) finish();
            }}
          >
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">{editor()}</div>
              <button
                type="button"
                title="Use the whole line"
                aria-label="Use the whole line"
                onClick={() => {
                  onReset();
                  finish();
                }}
                className="bg-muted flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg"
              >
                <RotateCcw className="size-4" />
              </button>
              <button
                type="button"
                onClick={finish}
                className="h-9 shrink-0 cursor-pointer rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white dark:bg-blue-500"
              >
                Done
              </button>
            </div>
            <div className="flex justify-between gap-3 text-xs">
              <span className={error != null ? 'text-destructive' : 'text-muted-foreground'}>
                {error ?? detail}
              </span>
              <span className="text-muted-foreground">{date}</span>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="line"
            {...fade}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5"
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate" title={description}>
                {description}
              </span>
              <CopyDescriptionButton text={description} />
            </span>
            {amountButton}
            <span className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-sm">{account}</span>
            <span className="text-muted-foreground text-right text-sm">{date}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
